
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getStallProductCount, getStallProducts, Stall } from "@/static-stores/stallStore";
import { MapPin, ShoppingBag, Users } from "lucide-react";
import { StarRating } from "../Stars";
import { Button } from "../ui/Button";
import { Card, CardContent } from "../ui/Card";
import { Badge } from "../ui/Badge";

export const StallCard = ({ stall }: { stall: Stall }) => {
  const router = useRouter();
  // Get the actual products for this stall
  const stallProducts = getStallProducts(stall.id);

  const handleProductClick = (productId: number) => {
    router.push(`/product/${productId}`);
  };

  const handleStallClick = () => {
    router.push(`/stall/${stall.id}`);
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-200 group">
      <CardContent className="p-6">
        {/* Header section with logo and info */}

        <div className="flex">
          <div className="mb-3 flex w-full">
            <h3
              onClick={handleStallClick}
              className="flex flex-1/2 pr-4 font-semibold text-lg group-hover:text-blue-600 transition-colors mb-2 cursor-pointer"
            >
              {stall.name}
            </h3>
            <div className="flex justify-end mb-2 place-items-center">
              {stall.isVerified && (
              <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                Verified
              </Badge>
            )}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-4 mb-4">
          <Image
            src={stall.logo}
            alt={stall.name}
            width={120}
            height={120}
            onClick={handleStallClick}
            className="rounded-full border shadow-sm group-hover:shadow-md transition-shadow object-cover flex-shrink-0 cursor-pointer"
          />
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{stall.location}</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <div className="flex-shrink-0 w-[85%] xs:w-auto">
                <StarRating rating={stall.rating} />
              </div>
              <span className="whitespace-nowrap">({stall.reviewCount})</span>
            </div>
          </div>
        </div>

        {/* Main content section - full width */}
        <div className="flex-1">
          <div className="mb-3">
            <p className="text-gray-600 text-sm line-clamp-2 h-10 flex items-start">{stall.description}</p>
          </div>

          <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
            <div className="flex flex-1/2 items-center gap-1">
              <ShoppingBag className="w-4 h-4" />
              {getStallProductCount(stall.id)} products
            </div>
            <div className="flex flex-1/2 justify-end items-center gap-1">
              <Users className="w-4 h-4" />
              {stall.followerCount} followers
            </div>
          </div>

          {/* Popular Products Section */}
          {stallProducts.length > 0 && (
            <div className="mb-4">
              <h4 className="font-bold mb-2 text-sm text-gray-900">
                Popular Products:
              </h4>
              <ul className="space-y-1">
                {stallProducts.slice(0, 3).map((product) => (
                  <li key={product.id}>
                    <button
                      onClick={() => handleProductClick(product.id)}
                      className="cursor-pointer text-sm hover:underline hover:text-blue-600 transition-colors text-left"
                    >
                      {product.title} — {product.price}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex flex-1/2">
              <Button asChild size="sm">
                <Link href={`/stall/${stall.id}`}>View Stall</Link>
              </Button>
            </div>
            <div className="flex flex-1/2 justify-end">
              <Button variant="outline" size="sm">Follow</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
